import React, { useEffect, useState } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useGodAuth } from '../../context/GodAuthContext';
import { godMetricsApi } from '../../api';
import { GS, G, STATUS_META, StatusPill } from './_godTheme';

function StatTile({ label, value, color, sub, onClick, urgent }) {
  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: '14px',
      border: `1px solid ${urgent ? color : '#E5E7EB'}`,
      padding: '18px 16px', cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.12s, box-shadow 0.12s',
      position: 'relative', overflow: 'hidden',
      boxShadow: urgent ? `0 0 0 1px ${color}` : '0 1px 4px rgba(0,0,0,0.04)',
    }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)'; }}}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = urgent ? `0 0 0 1px ${color}` : '0 1px 4px rgba(0,0,0,0.04)'; }}
    >
      {urgent && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: color }} />}
      <p style={{ fontSize: '28px', fontWeight: 800, color, letterSpacing: '-0.04em', lineHeight: 1, marginBottom: '4px' }}>{value ?? 0}</p>
      <p style={{ fontSize: '12px', color: G.textMuted, fontWeight: 500 }}>{label}</p>
      {sub && <p style={{ fontSize: '11px', color: G.textFaint, marginTop: '3px' }}>{sub}</p>}
    </div>
  );
}

function StatusBar({ breakdown, total }) {
  if (!breakdown || total === 0) return null;
  return (
    <div style={{ display: 'flex', height: '8px', borderRadius: '999px', overflow: 'hidden', gap: '2px', marginTop: '12px' }}>
      {breakdown.map(item => item.count > 0 && (
        <div key={item.status} style={{ flex: item.count, background: item.color, borderRadius: '999px' }} title={`${item.status}: ${item.count}`} />
      ))}
    </div>
  );
}

function ExpiryRow({ company, onClick }) {
  const days  = company.daysUntilExpiry;
  const color = days <= 7 ? '#DC2626' : days <= 14 ? '#D97706' : '#B45309';
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', background: G.bg, borderRadius: '10px',
      cursor: 'pointer', gap: '12px',
      border: `1px solid ${color}33`,
      transition: 'border-color 0.15s, background 0.12s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = '#fff'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}33`; e.currentTarget.style.background = G.bg; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: G.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.name}</p>
        <p style={{ fontSize: '11px', color: G.textMuted, marginTop: '2px' }}>{company.slug} · {company.employeeCount} employees</p>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <p style={{ fontSize: '13px', fontWeight: 700, color }}>{days}d left</p>
        <p style={{ fontSize: '10px', color: G.textFaint, marginTop: '1px' }}>{company.subscriptionEnd}</p>
      </div>
    </div>
  );
}

function SignupRow({ company, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '9px 12px', borderRadius: '10px', cursor: 'pointer',
      transition: 'background 0.1s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = G.bg}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px',
        background: G.goldLight, color: G.gold,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '14px', fontWeight: 700, flexShrink: 0,
      }}>{company.name?.[0]?.toUpperCase()}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: G.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.name}</p>
        <p style={{ fontSize: '11px', color: G.textMuted }}>{company.plan}</p>
      </div>
      <StatusPill status={company.status} />
    </div>
  );
}

// ── MRR calculator ─────────────────────────────────────────────────────────────
function MRRCard({ active, trial }) {
  const PLAN_PRICE = 3499;
  const mrr = (active || 0) * PLAN_PRICE;
  const arr = mrr * 12;
  return (
    <div style={{ ...GS.card, background: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 60%, #6366F1 100%)', color: '#fff', border: 'none' }}>
      <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.6)', marginBottom: '14px' }}>Revenue Snapshot</p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {[
          { label: 'MRR', value: `₹${mrr.toLocaleString('en-IN')}` },
          { label: 'ARR run-rate', value: `₹${(arr/100000).toFixed(1)}L` },
          { label: 'Paying', value: active || 0 },
          { label: 'Trial', value: trial || 0 },
        ].map((m, i) => (
          <div key={i} style={{ flex: '1 1 0', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 12px', minWidth: '80px' }}>
            <p style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1 }}>{m.value}</p>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', marginTop: '3px', fontWeight: 500 }}>{m.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GodDashboard() {
  const { godUser }        = useGodAuth();
  const navigate           = useNavigate();
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState('');

  useEffect(() => {
    godMetricsApi.dashboard()
      .then(r => setData(r.data.data))
      .catch(() => setError('Failed to load metrics'))
      .finally(() => setLoad(false));
  }, []);

  const go = (status) => navigate(status ? `/god/companies?status=${status}` : '/god/companies');

  return (
    <div style={{ maxWidth: '960px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={GS.title}>Welcome back, {godUser?.username}</h1>
        <p style={GS.sub}>Platform Control Centre · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
      </div>

      {error && <div style={GS.errBox}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: G.textMuted }}>
          <div style={{ fontSize: '24px', marginBottom: '12px', animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</div>
          <p>Loading metrics…</p>
        </div>
      ) : data && (<>
        {/* MRR card */}
        <div style={{ marginBottom: '16px' }}>
          <MRRCard active={data.active} trial={data.trial} />
        </div>

        {/* Stat grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          <StatTile label="Total Companies" value={data.total}          color={G.gold}      onClick={() => go()} />
          <StatTile label="Active"          value={data.active}         color="#16A34A"     onClick={() => go('ACTIVE')} />
          <StatTile label="On Trial"        value={data.trial}          color="#D97706"     onClick={() => go('TRIAL')} />
          <StatTile label="Suspended"       value={data.suspended}      color="#DC2626"     onClick={() => go('SUSPENDED')} urgent={data.suspended > 0} />
          <StatTile label="Employees"       value={data.totalEmployees} color="#2563EB"     sub="all tenants" />
          <StatTile label="New this month"  value={data.newThisMonth}   color="#6366F1"     onClick={() => go()} />
        </div>

        {/* Status distribution bar */}
        {data.total > 0 && (
          <div style={{ ...GS.card, marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <p style={GS.cardTitle}>Company Status Distribution</p>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                {data.statusBreakdown?.map(item => item.count > 0 && (
                  <span key={item.status} style={{ fontSize: '12px', color: G.textMuted, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, display: 'inline-block' }} />
                    <span style={{ color: item.color, fontWeight: 700 }}>{item.count}</span> {item.status}
                  </span>
                ))}
              </div>
            </div>
            <StatusBar breakdown={data.statusBreakdown} total={data.total} />
          </div>
        )}

        {/* Two column */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
          {/* Expiring soon */}
          <div style={GS.card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <p style={GS.cardTitle}>Expiring Soon</p>
              <span style={{ fontSize: '11px', color: '#DC2626', fontWeight: 700 }}>{data.expiringSoon} within 30 days</span>
            </div>
            {!data.expiringSoonList?.length
              ? <p style={{ fontSize: '13px', color: G.textMuted, padding: '10px 0' }}>All subscriptions healthy ✓</p>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {data.expiringSoonList.map(c => <ExpiryRow key={c.id} company={c} onClick={() => navigate(`/god/companies/${c.id}`)} />)}
                </div>
            }
          </div>

          {/* Recent signups */}
          <RecentSignupsPanel navigate={navigate} />
        </div>
      </>)}
    </div>
  );
}

function RecentSignupsPanel({ navigate }) {
  const [signups,  setSignups]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  useEffect(() => {
    godMetricsApi.recentSignups(5).then(r => setSignups(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div style={GS.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <p style={GS.cardTitle}>Recent Signups</p>
        <button onClick={() => navigate('/god/companies')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: G.gold, fontWeight: 600 }}>View all →</button>
      </div>
      {loading
        ? <p style={{ fontSize: '13px', color: G.textMuted }}>Loading…</p>
        : !signups.length
          ? <p style={{ fontSize: '13px', color: G.textMuted }}>No companies yet.</p>
          : <div style={{ display: 'flex', flexDirection: 'column' }}>
              {signups.map(c => <SignupRow key={c.id} company={c} onClick={() => navigate(`/god/companies/${c.id}`)} />)}
            </div>
      }
    </div>
  );
}
